import { useState } from 'react';

export interface ValidationResult {
    isValid: boolean;
    errors: string[];
    validFiles: File[];
}

const validateImageFiles = (files: FileList | File[], options: {
    maxFileSize?: number; // in bytes
    allowedTypes?: string[];
    maxFiles?: number;
} = {}): ValidationResult => {
    const {
        maxFileSize = 10 * 1024 * 1024, // 10MB default
        allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'],
        maxFiles
    } = options;

    const fileArray = Array.from(files);
    const errors: string[] = [];
    const validFiles: File[] = [];

    // Check file count
    if (maxFiles && fileArray.length > maxFiles) {
        errors.push(`Maximum ${maxFiles} files allowed. You selected ${fileArray.length}.`);
        return { isValid: false, errors, validFiles };
    }

    fileArray.forEach((file, index) => {
        const fileErrors: string[] = [];

        // Check if file is actually an image by MIME type and extension
        const isImageType = allowedTypes.includes(file.type);
        const isImageExtension = /\.(jpg|jpeg|png|gif|webp|svg|bmp)$/i.test(file.name);

        if (!isImageType || !isImageExtension) {
            fileErrors.push(`"${file.name}" is not a valid image file.`);
        }

        // Check file size
        if (file.size > maxFileSize) {
            const sizeInMB = (file.size / 1024 / 1024).toFixed(2);
            const maxSizeInMB = (maxFileSize / 1024 / 1024).toFixed(2);
            fileErrors.push(`"${file.name}" (${sizeInMB}MB) exceeds maximum size of ${maxSizeInMB}MB.`);
        }

        // Check if file is actually an image by reading its magic bytes
        // This is a basic check - for production, you might want a more robust solution
        if (file.size === 0) {
            fileErrors.push(`"${file.name}" is empty.`);
        }

        if (fileErrors.length === 0) {
            validFiles.push(file);
        } else {
            errors.push(...fileErrors);
        }
    });

    return {
        isValid: errors.length === 0,
        errors,
        validFiles
    };
};

// More advanced validation using file signature (magic bytes)
const getFileSignature = (file: File): Promise<string> => {
    return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            const buffer = e.target?.result as ArrayBuffer;
            const uint8Array = new Uint8Array(buffer.slice(0, 4));
            const signature = Array.from(uint8Array)
                .map(byte => byte.toString(16).padStart(2, '0'))
                .join('')
                .toUpperCase();
            resolve(signature);
        };
        reader.readAsArrayBuffer(file.slice(0, 4));
    });
};

const signatureToMimeType: { [key: string]: string } = {
    'FFD8FF': 'image/jpeg',
    '89504E47': 'image/png',
    '47494638': 'image/gif',
    '52494646': 'image/webp',
    '3C3F786D': 'image/svg+xml',
    '424D': 'image/bmp'
};

export const validateFileSignature = async (file: File): Promise<boolean> => {
    try {
        const signature = await getFileSignature(file);

        // Check if signature matches known image file signatures
        const isValidSignature = Object.keys(signatureToMimeType).some(sig =>
            signature.startsWith(sig)
        );

        return isValidSignature;
    } catch (error) {
        console.error('Error validating file signature:', error);
        return false;
    }
};

export default validateImageFiles;